import { ref, computed } from 'vue'
import { apiClient } from '@/api/client'

export interface TelegramBindingStatus {
  isBound: boolean
  telegramUsername: string | null
}

export function useTelegramBinding() {
  const loading = ref(false)
  const error = ref<string | null>(null)
  const status = ref<TelegramBindingStatus | null>(null)
  const bindCode = ref<string | null>(null)
  // Telegram выключен на бэкенде (TELEGRAM_ENABLED): предложение привязки не показываем.
  // null - статус ещё не загружен
  const available = ref<boolean | null>(null)

  async function fetchStatus() {
    loading.value = true
    error.value = null
    try {
      const response = await apiClient.get('/api/telegram/status')
      const data = response.data
      if (data.success) {
        status.value = data.data
        available.value = data.data?.available !== false
      } else {
        error.value = data.message
        available.value = true
      }
    } catch {
      error.value = 'Ошибка загрузки статуса'
      // Ошибку загрузки показываем, а не прячем вместе с блоком
      available.value = true
    } finally {
      loading.value = false
    }
  }

  async function generateBindCode() {
    loading.value = true
    error.value = null
    bindCode.value = null
    try {
      const response = await apiClient.post('/api/telegram/bind-code')
      const data = response.data
      if (data.success) {
        bindCode.value = data.data.code
        status.value = {
          isBound: false,
          telegramUsername: null,
        }
      } else {
        error.value = data.message
      }
    } catch {
      error.value = 'Ошибка генерации кода'
    } finally {
      loading.value = false
    }
  }

  async function unbind() {
    loading.value = true
    error.value = null
    try {
      const response = await apiClient.post('/api/telegram/unbind')
      const data = response.data
      if (data.success) {
        status.value = {
          isBound: false,
          telegramUsername: null,
        }
        bindCode.value = null
      } else {
        error.value = data.message
      }
    } catch {
      error.value = 'Ошибка отвязки'
    } finally {
      loading.value = false
    }
  }

  const botLink = computed(() => {
    if (!bindCode.value) return null

    // Имя бота из окружения: захардкоженный LabActiveBot вёл в чужой или несуществующий
    // чат на любом стенде, кроме того, где бот так назывался.
    const botUsername = import.meta.env['VITE_TELEGRAM_BOT_USERNAME']
    if (!botUsername) return null

    return `https://t.me/${botUsername}?start=${bindCode.value}`
  })

  return {
    loading,
    error,
    status,
    available,
    bindCode,
    botLink,
    fetchStatus,
    generateBindCode,
    unbind,
  }
}
